// frontend/src/features/agency/Dashboard.js
// Agency dashboard shell:
// - Loads agency context (agencies list / limits, etc.)
// - Provides sidebar navigation (Active / Reserved / Banned / Available)
// - NO driver hydration here (lists + socket.js own freshness)
//
// Phase 8.7C+ rule:
// - Lane membership is backend-derived (assignment/contract read models).
// - Dashboard stays a pure shell (like UPS).

import React, { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgencies, selectAgenciesLoading } from '../agencies/agenciesSlice';
import { LayoutList, Slash, UserCheck, Menu, X, Box } from 'lucide-react';

// Phase 8.7D: replace stats + site selector header with a unified banner header
import AgencyDriverHeader from './AgencyDriverHeader';


export default function AgencyDashboard() {

  const [collapsed, setCollapsed] = useState(false);
  const dispatch = useDispatch();
  const loading = useSelector(selectAgenciesLoading);

  // Load agency data once on mount
  useEffect(() => {
    dispatch(fetchAgencies());
  }, [dispatch]);

  // Don’t render any child route until agencies have loaded
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading agency data…
      </div>
    );
  }

  const btnBase = 'block w-full text-left px-4 py-2 rounded transition-all';

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          collapsed ? 'w-16' : 'w-64'
        } bg-white border-r shadow-sm flex flex-col transition-all duration-200`}
      >
        <div className="p-4 flex items-center justify-between">
          {!collapsed && <h2 className="text-xl font-bold">Agency</h2>}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded hover:bg-gray-200"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Menu size={20} /> : <X size={20} />}
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-1">
          {/* Active */}
          <NavLink
            to="/agency/drivers"
            end
            className={({ isActive }) =>
              `${btnBase} ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              } flex items-center`
            }
          >
            <LayoutList className="mr-2" />
            {!collapsed && 'Active Drivers'}
          </NavLink>

          {/* Reserved */}
          <NavLink
            to="/agency/reserved"
            className={({ isActive }) =>
              `${btnBase} ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              } flex items-center`
            }
          >
            <UserCheck className="mr-2" />
            {!collapsed && 'Reserved Drivers'}
          </NavLink>

          {/* Available */}
          <NavLink
            to="/agency/available"
            className={({ isActive }) =>
              `${btnBase} ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              } flex items-center`
            }
          >
            <Box className="mr-2" />
            {!collapsed && 'Available Drivers'}
          </NavLink>

          {/* Banned */}
          <NavLink
            to="/agency/banned"
            className={({ isActive }) =>
              `${btnBase} ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              } flex items-center`
            }
          >
            <Slash className="mr-2" />
            {!collapsed && 'Banned Drivers'}
          </NavLink>
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-6 overflow-y-auto">
        {/* Header row: banner (shared across all tabs) */}
        <AgencyDriverHeader />

        <Outlet />
      </main>
    </div>
  );
}
